/**
 * Correlates the AI-summary generation "start" (task 5.17,
 * `containers/report/details/index.tsx`) with its terminal success/error
 * observed later over the shared `snap-status` WebSocket
 * (`components/PendingReportSockets.tsx`), so the catalog `ai_summary`
 * measurement carries an accurate `durationMs`.
 *
 * Keyed by report id — "latest wins" per id, mirroring
 * `perceivedLatency.helper`'s `recordCreateIssued` semantics. Only one
 * summary generation is in flight per report at a time in practice (the UI
 * gates re-generation via `isGeneratingSummary`), so a simple last-write-wins
 * map is sufficient; no report id ever leaves this module.
 */

const startTimestamps = new Map<string, number>();

/** Records the start of a summary generation for `reportId`. */
export function recordSummaryStart(reportId: string): void {
  if (!reportId) return;
  startTimestamps.set(reportId, performance.now());
}

/**
 * Consumes (deletes) the recorded start for `reportId` and returns the
 * elapsed duration in whole milliseconds, or `null` when no start was
 * recorded or the elapsed value is somehow non-finite/negative — callers
 * should omit `durationMs` from the emitted event in that case rather than
 * report a false measurement.
 */
export function consumeSummaryDuration(reportId: string | undefined | null): number | null {
  if (!reportId) return null;
  const start = startTimestamps.get(reportId);
  if (start === undefined) return null;
  startTimestamps.delete(reportId);
  const durationMs = Math.round(performance.now() - start);
  return Number.isFinite(durationMs) && durationMs >= 0 ? durationMs : null;
}
